import React, { ChangeEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  CardMedia,
  IconButton,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const AddDispatcher = () => {
  const navigate = useNavigate();

  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  const [difficulty, setDifficulty] = useState("default");

  const [weeks, setWeeks] = useState([
    {
      weekTitle: "Week 1",
      unlockWeek: 1,
      workouts: [""], // empty workout input to start
    },
  ]);

  const handleDifficulty = (event: SelectChangeEvent) => {
    setDifficulty(event.target.value as string);
  };

  // Add a new week
  const addNewWeek = () => {
    setWeeks([
      ...weeks,
      {
        weekTitle: `Week ${weeks.length + 1}`,
        unlockWeek: weeks.length + 1,
        workouts: [""],
      },
    ]);
  };

  // Remove a week
  const removeWeek = (index: number) => {
    const updated = [...weeks];
    updated.splice(index, 1);
    setWeeks(updated);
  };

  // Add workout in a week
  const addWorkout = (weekIndex: number) => {
    const updated = [...weeks];
    updated[weekIndex].workouts.push("");
    setWeeks(updated);
  };

  // Update workout name
  const updateWorkout = (
    weekIndex: number,
    workoutIndex: number,
    value: string
  ) => {
    const updated = [...weeks];
    updated[weekIndex].workouts[workoutIndex] = value;
    setWeeks(updated);
  };

  return (
    <>
      <div className="main_title">
        <h1>Add Program</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-programs")}>
            Manage Programs
          </button>{" "}
          - Add Program
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            {/* Program Thumbnail */}
            <div className="control_group w_33">
              <label>Program Thumbnail*</label>

              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={image}
                        alt="Uploaded Thumbnail"
                      />
                    </figure>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setImage("");
                        setFileName("");
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              ) : (
                <label htmlFor="icon-button-file" className="upload_image">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{ accept: "image/*" }}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const files = e.target.files;
                      if (files && files[0].type.includes("image")) {
                        setFileName(files[0].name);
                        setImage(URL.createObjectURL(files[0]));
                      }
                    }}
                  />

                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="placeholder"
                      />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            {/* Program Title */}
            <div className="control_group w_33">
              <label>Program Title*</label>
              <TextField placeholder="Enter Program Title" fullWidth />
            </div>

            {/* Duration */}
            <div className="control_group w_33">
              <label>Duration*</label>
              <TextField placeholder="12 Weeks / 6 Weeks" fullWidth />
            </div>

            {/* Difficulty */}
            <div className="control_group w_33">
              <label>Difficulty*</label>
              <Select fullWidth value={difficulty} onChange={handleDifficulty}>
                <MenuItem disabled value="default">
                  Select Difficulty
                </MenuItem>
                <MenuItem value="Beginner">Beginner</MenuItem>
                <MenuItem value="Intermediate">Intermediate</MenuItem>
                <MenuItem value="Advanced">Advanced</MenuItem>
              </Select>
            </div>

            {/* Equipment Needed */}
            <div className="control_group w_33">
              <label>Equipment Needed*</label>
              <TextField fullWidth placeholder="Dumbbells, Mat, None..." />
            </div>
            {/* Enrolled Users (Optional – admin reference) */}
            <div className="control_group w_33">
              <label>Enrolled Users</label>
              <TextField fullWidth type="number" placeholder="0" />
            </div>

            {/* Goals */}
            <div className="control_group w_100">
              <label>Goals*</label>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Strength, Fat Loss, Endurance..."
              />
            </div>

            {/* Description */}
            <div className="control_group w_100">
              <label>Description*</label>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter Program Description"
              />
            </div>

            {/* -------------------- WEEK BUILDER -------------------- */}

            <div className="w_100">
              <div
                className="main_title"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <h1>Program Weeks & Workouts</h1>
                {/* Add New Week Button */}
                <div>
                  <Button
                    startIcon={<AddIcon />}
                    className="btn btn_secondary"
                    onClick={addNewWeek}
                  >
                    Add New Week
                  </Button>
                </div>
              </div>
              {weeks.map((week, weekIndex) => (
                <div className="week_block " key={weekIndex}>
                  {/* Week Header */}
                  <div className="week_header">
                    <h3>{week.weekTitle}</h3>

                    <IconButton
                      size="small"
                      onClick={() => removeWeek(weekIndex)}
                      disabled={weeks.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                  <div className="form">
                    <div className="gap_p">
                      {/* Unlock Week (Drip) */}
                      <div className="control_group w_50">
                        <label>Unlock Week*</label>
                        <TextField
                          fullWidth
                          type="number"
                          value={week.unlockWeek}
                          onChange={(e) => {
                            const updated = [...weeks];
                            updated[weekIndex].unlockWeek = Number(
                              e.target.value
                            );
                            setWeeks(updated);
                          }}
                        />
                      </div>

                      {/* Workouts */}
                      <div className="control_group w_50">
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "10px",
                            alignItems: "center",
                            transform: "translateY(-5px)",
                          }}
                        >
                          <label>Workouts for this Week</label>
                          <Button
                            startIcon={<AddIcon />}
                            className="btn btn_primary sm"
                            onClick={() => addWorkout(weekIndex)}
                          >
                            Add More
                          </Button>
                        </div>

                        {week.workouts.map((workout, workoutIndex) => (
                          <div className="control_group ">
                            <Select
                              fullWidth
                              value={difficulty}
                              onChange={handleDifficulty}
                            >
                              <MenuItem disabled value="default">
                                Select Workout
                              </MenuItem>
                              <MenuItem value="Beginner">Chest & Triceps</MenuItem>
                              <MenuItem value="Intermediate">
                                Back & Biceps
                              </MenuItem>
                              <MenuItem value="Advanced">Legs</MenuItem>
                              <MenuItem value="Advanced">Shoulder Day</MenuItem>
                              <MenuItem value="Advanced">Full Body Workout</MenuItem>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="form_btn">
            <Button className="btn btn_primary">Save Program</Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddDispatcher;
